import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Component: Payment Method Tabs
const PaymentOptions = ({ selected, setSelected }) => {
  const options = ['UPI', 'Net Banking', 'Card'];
  return (
    <Box display="flex" justifyContent="center" gap={2} mb={4}>
      {options.map((method) => (
        <Button
          key={method}
          variant={selected === method ? 'contained' : 'outlined'}
          onClick={() => setSelected(method)}
        >
          {method}
        </Button>
      ))}
    </Box>
  );
};

// UPI Payment Form
const UpiPayment = ({ formData, setFormData }) => (
  <TextField
    fullWidth
    label="UPI ID"
    placeholder="example@upi"
    value={formData.upiId || ''}
    onChange={(e) => setFormData({ ...formData, upiId: e.target.value })}
    sx={{ mb: 3 }}
  />
);

// Net Banking Form
const NetBankingForm = ({ formData, setFormData }) => (
  <>
    <Select
      fullWidth
      displayEmpty
      value={formData.bank || ''}
      onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
      sx={{ mb: 3 }}
    >
      <MenuItem value="" disabled>
        Select Bank
      </MenuItem>
      <MenuItem value="HDFC">HDFC</MenuItem>
      <MenuItem value="SBI">SBI</MenuItem>
      <MenuItem value="ICICI">ICICI</MenuItem>
    </Select>

    <TextField
      fullWidth
      label="Account Number"
      value={formData.accountNumber || ''}
      onChange={(e) =>
        setFormData({ ...formData, accountNumber: e.target.value })
      }
      sx={{ mb: 3 }}
    />
  </>
);

// Card Payment Placeholder
const CardPaymentPlaceholder = () => (
  <Alert severity="info" sx={{ mb: 3 }}>
    Card payments coming soon. Please choose UPI or Net Banking.
  </Alert>
);

// Status Component
const PaymentStatus = ({ status, receipt }) => {
  if (status === 'loading')
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography color="primary">Processing payment...</Typography>
      </Box>
    );

  if (status === 'success')
    return (
      <Alert severity="success">
        Payment Successful! Transaction ID: <strong>{receipt}</strong>
      </Alert>
    );

  if (status === 'failed')
    return (
      <Alert severity="error">Payment Failed. Please try again.</Alert>
    );

  return null;
};

// Main Support Page Component
const SupportPage = () => {
  const [selectedMethod, setSelectedMethod] = useState('UPI');
  const [formData, setFormData] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);

  const handlePayment = () => {
    setError('');
    setPaymentStatus(null);

    if (
      (selectedMethod === 'UPI' && !formData.upiId) ||
      (selectedMethod === 'Net Banking' &&
        (!formData.bank || !formData.accountNumber))
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setPaymentStatus('loading');
    setTimeout(() => {
      const success = Math.random() > 0.2;
      if (success) {
        setPaymentStatus('success');
        setReceipt(`TXN${Date.now()}`);
      } else {
        setPaymentStatus('failed');
      }
    }, 2000);
  };

  return (
    <>
    <Header />
    <Box maxWidth="sm" mx="auto" px={2} py={6}>
      <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
        Support Samriddhi Trust
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" mb={4}>
        Choose a payment method and contribute to a better future.
      </Typography>

      <PaymentOptions selected={selectedMethod} setSelected={setSelectedMethod} />

      <Paper elevation={3} sx={{ p: 4 }}>
        {selectedMethod === 'UPI' && (
            <UpiPayment formData={formData} setFormData={setFormData} />
        )}
        {selectedMethod === 'Net Banking' && (
            <NetBankingForm formData={formData} setFormData={setFormData} />
        )}
        {selectedMethod === 'Card' && <CardPaymentPlaceholder />}

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {paymentStatus ? (
            <PaymentStatus status={paymentStatus} receipt={receipt} />
        ) : (
            <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handlePayment}
            sx={{ mt: 2 }}
            >
            Proceed to Pay
          </Button>
        )}
      </Paper>
    </Box>
    <Footer />    
    </>
  );
};

export default SupportPage;
